import { useCallback, useEffect, useRef, useState } from 'react';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    ILogger,
    LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';
import { useAxiosSwagger } from './useAxiosSwagger';
import { queueHubUrl } from '../api/baseUrl';
import {
    QueueConnectionState,
    QueueSnapshot,
    getQueueSnapshot,
} from '../api/queueMonitor';
import { describeError } from '../api/requeue';

/** The hub methods, exactly as they are named in QueueMonitorHub. */
const WATCH = 'watch';
const UNWATCH = 'unwatch';
const REFRESH = 'refresh';
const SNAPSHOT = 'queueSnapshot';

/** How often we fall back to polling when the websocket cannot get through. */
const POLL_INTERVAL_MS = 5000;

/**
 * How long a tab may sit in the background before we tell the hub we are not watching. Any
 * open tab costs the API one measurement every two seconds, so a forgotten tab would keep
 * querying Azure Storage forever.
 */
const IDLE_AFTER_MS = 30000;

/**
 * Logger that swallows one specific message.
 *
 * In development mode React runs every effect twice to surface cleanup bugs. For a
 * websocket that means: open, abort immediately, open again. The aborted attempt logs
 * itself as "The connection was stopped during negotiation", which looks like a failure
 * but is not one - the second attempt simply succeeds. Only that line is dropped;
 * everything else stays in the console.
 */
const quietLogger: ILogger = {
    log(logLevel, message) {
        if (
            logLevel < LogLevel.Warning ||
            message.includes('stopped during negotiation')
        ) {
            return;
        }

        // eslint-disable-next-line no-console
        console.warn(`[queue-hub] ${message}`);
    },
};

export interface QueueMonitor {
    snapshot: QueueSnapshot | null;
    connection: QueueConnectionState;
    error: string | null;
    refresh: () => void;
}

/**
 * Tracks the queues of a single customer environment live.
 *
 * The hub pushes a new state as soon as something changes. When the websocket does not
 * come up - a proxy blocking it, an API without a hub - this hook switches to REST polling
 * on its own, with exactly the same snapshot. The UI notices nothing beyond the status
 * indicator.
 */
export const useQueueMonitor = (
    customer?: string,
    environment?: string
): QueueMonitor => {
    const axios = useAxiosSwagger();
    const { getAccessTokenSilently } = useAuth();

    const [snapshot, setSnapshot] = useState<QueueSnapshot | null>(null);
    const [connection, setConnection] = useState<QueueConnectionState>('idle');
    const [error, setError] = useState<string | null>(null);

    const hubRef = useRef<HubConnection | null>(null);

    const apply = useCallback((next: QueueSnapshot) => {
        setSnapshot(next);
        setError(next.error ?? null);
    }, []);

    // The connection itself. Everything hangs off one effect: for another customer or
    // environment a new connection is cheaper than the bookkeeping needed to move the same
    // connection to another group cleanly.
    useEffect(() => {
        if (!customer || !environment || !axios) {
            return;
        }

        let cancelled = false;
        let pollTimer: ReturnType<typeof setTimeout> | undefined;
        let idleTimer: ReturnType<typeof setTimeout> | undefined;

        /** Fallback: the same snapshot, just fetched ourselves. */
        const poll = async () => {
            if (cancelled) {
                return;
            }

            try {
                const next = await getQueueSnapshot(axios, customer, environment);

                if (!cancelled) {
                    apply(next);
                    setConnection('polling');
                }
            } catch (ex) {
                if (!cancelled) {
                    setError(describeError(ex));
                    setConnection('offline');
                }
            }

            if (!cancelled) {
                pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
            }
        };

        const hub = new HubConnectionBuilder()
            .withUrl(queueHubUrl, {
                accessTokenFactory: () => getAccessTokenSilently(),
            })
            .withAutomaticReconnect()
            .configureLogging(quietLogger)
            .build();

        hub.on(SNAPSHOT, (next: QueueSnapshot) => {
            if (!cancelled) {
                apply(next);
            }
        });

        hub.onreconnecting(() => !cancelled && setConnection('reconnecting'));

        hub.onreconnected(async () => {
            if (cancelled) {
                return;
            }

            setConnection('live');

            // After a reconnect this is a new connection id, so the group from before no
            // longer knows us.
            try {
                apply(await hub.invoke<QueueSnapshot>(WATCH, customer, environment));
            } catch (ex) {
                setError(describeError(ex));
            }
        });

        hub.onclose(() => {
            if (cancelled) {
                return;
            }

            // Automatic reconnecting has given up; from here on we poll ourselves.
            setConnection('polling');
            poll();
        });

        /** Tell the hub we stopped watching, and pick it back up later. */
        const stopWatching = () => {
            if (hub.state === HubConnectionState.Connected) {
                hub.invoke(UNWATCH, customer, environment).catch(() => undefined);
            }
        };

        const resumeWatching = async () => {
            if (cancelled || hub.state !== HubConnectionState.Connected) {
                return;
            }

            try {
                apply(await hub.invoke<QueueSnapshot>(WATCH, customer, environment));
            } catch (ex) {
                setError(describeError(ex));
            }
        };

        const handleVisibility = () => {
            clearTimeout(idleTimer);

            if (document.visibilityState === 'hidden') {
                idleTimer = setTimeout(stopWatching, IDLE_AFTER_MS);
                return;
            }

            resumeWatching();
        };

        document.addEventListener('visibilitychange', handleVisibility);

        (async () => {
            setConnection('connecting');
            setError(null);

            try {
                await hub.start();

                if (cancelled) {
                    return;
                }

                hubRef.current = hub;
                setConnection('live');

                // The hub answers with the current state, so the screen fills right away.
                apply(await hub.invoke<QueueSnapshot>(WATCH, customer, environment));
            } catch (ex) {
                if (cancelled) {
                    return;
                }

                // No websocket available. Annoying but not fatal: the numbers are just as
                // correct when we fetch them ourselves, only a bit slower.
                console.warn('Queue-hub niet beschikbaar, terugval op ophalen:', ex);
                poll();
            }
        })();

        return () => {
            cancelled = true;
            clearTimeout(pollTimer);
            clearTimeout(idleTimer);
            document.removeEventListener('visibilitychange', handleVisibility);
            hubRef.current = null;
            hub.stop().catch(() => undefined);
        };
    }, [axios, customer, environment, getAccessTokenSilently, apply]);

    const refresh = useCallback(() => {
        const hub = hubRef.current;

        (async () => {
            try {
                if (hub && hub.state === HubConnectionState.Connected) {
                    apply(
                        await hub.invoke<QueueSnapshot>(
                            REFRESH,
                            customer as string,
                            environment as string
                        )
                    );

                    return;
                }

                if (axios && customer && environment) {
                    apply(await getQueueSnapshot(axios, customer, environment));
                }
            } catch (ex) {
                setError(describeError(ex));
            }
        })();
    }, [axios, customer, environment, apply]);

    return { snapshot, connection, error, refresh };
};
